import _ from "lodash";

import {
  PERSONA_CHECKS_REASONS_MAP,
  PERSONA_VERIFICATION_PREFIXES,
  PERSONA_VERIFICATION_STATUS,
  PERSONA_VERIFICATION_TYPES,
  VERIFICATION_OF_RESIDENCY_STATUS,
  VERIFICATION_TYPE,
} from "shared/config/constants";

import {
  PERSONA_CHECK_STATUS,
  VerificationCheck,
} from "../../utils/interfaces";
import { TAG_COLORS } from "../Tag";

const UNKNOWN_REASON_DESCRIPTION =
  "Unmapped reason from provider — contact VERO support.";

export const mapFailureReasons = (checks) => {
  const failureReasonChecksMap = new Map();

  const addCheckForReasonDescription = (reasonDescription, checkName) => {
    const existingChecks = failureReasonChecksMap.get(reasonDescription);
    if (existingChecks) {
      existingChecks.add(checkName);
      failureReasonChecksMap.set(reasonDescription, existingChecks);
    } else {
      failureReasonChecksMap.set(reasonDescription, new Set([checkName]));
    }
  };

  checks.forEach((check) => {
    if (!check.reasons || check.reasons.length === 0) {
      addCheckForReasonDescription(UNKNOWN_REASON_DESCRIPTION, check.name);
    } else {
      check.reasons.forEach((reason) => {
        const description =
          PERSONA_CHECKS_REASONS_MAP[check.name]?.reasons[reason] ||
          _.capitalize(_.lowerCase(reason)).trim() ||
          UNKNOWN_REASON_DESCRIPTION;
        addCheckForReasonDescription(description, check.name);
      });
    }
  });

  return failureReasonChecksMap;
};

export const VERIFICATION_STATUS_INFO_FOR_TYPE = Object.freeze({
  [VERIFICATION_TYPE.ID]: {
    [PERSONA_VERIFICATION_STATUS.COMPLETED]: {
      label: "Identity Verified",
      color: TAG_COLORS.GREEN,
    },
    [PERSONA_VERIFICATION_STATUS.FAILED]: {
      label: "Identity Unverified",
      color: TAG_COLORS.RED,
    },
    [PERSONA_VERIFICATION_STATUS.EXPIRED]: {
      label: "Identity Expired",
    },
  },
});

export const VERIFICATION_ACCORDION_FOR_STATUS = Object.freeze({
  [PERSONA_VERIFICATION_STATUS.COMPLETED]: "verified",
  [PERSONA_VERIFICATION_STATUS.PASSED]: "verified",
  [PERSONA_VERIFICATION_STATUS.APPROVED]: "verified",
  [PERSONA_VERIFICATION_STATUS.FAILED]: "unverified",
  [PERSONA_VERIFICATION_STATUS.DECLINED]: "unverified",
  [VERIFICATION_OF_RESIDENCY_STATUS.SUBMITTED]: "verified",
  [VERIFICATION_OF_RESIDENCY_STATUS.WAITING]: "unverified",
  [VERIFICATION_OF_RESIDENCY_STATUS.STARTED]: "unverified",
  pending: "pending",
});

export const formatRowStr = (str: string, verificationType) => {
  const prefix = PERSONA_VERIFICATION_PREFIXES[verificationType];
  const formattedStr = prefix ? str.replace(prefix, "") : str;
  return formattedStr.replaceAll("_", " ");
};

/**
 * Normalizes Persona statuses to VERO's verification status system
 * Maps "approved" → "passed" and "declined" → "failed"
 * Provides fallback to "failed" for unknown statuses
 */
export const normalizePersonaStatus = (status: string): string => {
  if (!status) return PERSONA_VERIFICATION_STATUS.FAILED;

  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case "approved":
      return PERSONA_VERIFICATION_STATUS.PASSED;
    case "declined":
      return PERSONA_VERIFICATION_STATUS.FAILED;
    case "completed":
    case "passed":
    case "failed":
    case "expired":
      return normalizedStatus;
    default:
      // Fallback to "failed" for any unrecognized status to avoid "undefined" display
      return PERSONA_VERIFICATION_STATUS.FAILED;
  }
};

export const filterChecks = (
  verificationChecks: VerificationCheck[],
  personaCheckStatus: PERSONA_CHECK_STATUS
): VerificationCheck[] => {
  return verificationChecks.filter(
    (check) => check.status !== personaCheckStatus
  );
};

/**
 * AAMVA Identity Comparison Check Configuration
 * Defines the parent-child relationship for AAMVA identity verification checks
 */
const AAMVA_IDENTITY_COMPARISON_CONFIG = Object.freeze({
  PARENT_CHECK: "aamva_identity_comparison",
  CHILD_CHECKS: [
    "aamva_birthdate_comparison",
    "aamva_identification_number_comparison",
    "aamva_name_comparison",
  ],
});

/**
 * Applies display-only overrides to verification checks based on parent-child relationships
 * This function modifies the `requirement` field for display purposes only, without affecting
 * the underlying data integrity or persisted status values.
 *
 * @param checks - Array of verification checks to process
 * @returns Array of checks with display overrides applied
 */
export const applyDisplayOverrides = (
  checks: VerificationCheck[]
): VerificationCheck[] => {
  // Create a copy to avoid mutating the original array
  const processedChecks = [...checks];

  // Check if any AAMVA child checks have failed
  const hasFailedAamvaChild = processedChecks.some(
    (check) =>
      AAMVA_IDENTITY_COMPARISON_CONFIG.CHILD_CHECKS.includes(check.name) &&
      check.status === PERSONA_CHECK_STATUS.FAILED
  );

  // Apply override to parent check if child checks have failed
  processedChecks.forEach((check) => {
    if (
      check.name === AAMVA_IDENTITY_COMPARISON_CONFIG.PARENT_CHECK &&
      check.requirement === "not_required" &&
      check.status === PERSONA_CHECK_STATUS.FAILED &&
      hasFailedAamvaChild
    ) {
      // Override for display grouping only - promotes from yellow to red
      check.requirement = "required";
    }
  });

  return processedChecks;
};

export const mapReasonsByCheck = (
  checks: VerificationCheck[],
  verificationType: string = PERSONA_VERIFICATION_TYPES.GOVERNMENT_ID
): Map<string, string[]> => {
  // Apply display overrides before processing
  const processedChecks = applyDisplayOverrides(checks);

  // Use the same efficient logic as mapFailureReasons but invert the mapping
  const reasonToChecksMap = new Map<string, Set<string>>();

  const addCheckForReasonDescription = (
    reasonDescription: string,
    checkName: string
  ) => {
    const existingChecks = reasonToChecksMap.get(reasonDescription);
    if (existingChecks) {
      existingChecks.add(checkName);
      reasonToChecksMap.set(reasonDescription, existingChecks);
    } else {
      reasonToChecksMap.set(reasonDescription, new Set([checkName]));
    }
  };

  // Use the same logic as mapFailureReasons
  processedChecks
    .filter((c) => c.status === PERSONA_CHECK_STATUS.FAILED)
    .forEach((check) => {
      if (!check.reasons || check.reasons.length === 0) {
        addCheckForReasonDescription(UNKNOWN_REASON_DESCRIPTION, check.name);
      } else {
        check.reasons.forEach((reason) => {
          const description =
            PERSONA_CHECKS_REASONS_MAP[check.name]?.reasons[reason] ||
            _.capitalize(_.lowerCase(reason)).trim() ||
            UNKNOWN_REASON_DESCRIPTION;
          addCheckForReasonDescription(description, check.name);
        });
      }
    });

  // Convert to the required format: Check Label → Array of Reasons
  const out = new Map<string, string[]>();

  reasonToChecksMap.forEach((checksSet, reasonDescription) => {
    checksSet.forEach((checkName) => {
      const checkLabel = formatRowStr(checkName, verificationType);

      if (!out.has(checkLabel)) {
        out.set(checkLabel, []);
      }
      out.get(checkLabel)!.push(reasonDescription);
    });
  });

  return out;
};
